/**
 * "Generate extra step (AI)" — choosing which suggested step to add.
 *
 * The store asks the provider for a plan "beyond" the existing steps and
 * used to take the LAST step it returned, unchecked. 18 Oct 2026 (MEASURED,
 * Chad's board): the AI ended its new plan the same way as the existing
 * one, so the "extra" step repeated step 4 word for word. Now: the last
 * suggested step that is NOT already in the plan; none → nothing is added
 * and the card says so (no silent no-op, CLAUDE.md §2).
 */
import type { ActionStep } from '../types';

/** Compare steps by their words only: case, punctuation and spacing ignored. */
export function normaliseStepText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

export function pickExtraStep(
  existing: readonly Pick<ActionStep, 'text'>[],
  suggested: readonly Omit<ActionStep, 'id' | 'done'>[],
): Omit<ActionStep, 'id' | 'done'> | undefined {
  const seen = new Set(existing.map((s) => normaliseStepText(s.text)));
  for (let i = suggested.length - 1; i >= 0; i -= 1) {
    const key = normaliseStepText(suggested[i].text);
    if (key && !seen.has(key)) return suggested[i];
  }
  return undefined;
}

export const NO_NEW_STEP_NOTICE =
  'The AI only suggested steps already in this plan, so nothing was added. Try “Update this plan” and say what’s missing.';

/**
 * Why a plan action fell back to on-device steps (18 Oct 2026 — CLAUDE.md §2:
 * at 04:38 an extra step came from the on-device templates and nothing on
 * screen said so, so the cause — safety refusal, daily limit, network —
 * could not be told apart). GeminiProvider records the reason in
 * `lastError` ("Gemini unavailable — used local engine fallback: <reason>")
 * and degrades instead of throwing; this turns it into one line for the card.
 * A provider with no `lastError` (Gemini answered, or the on-device engine
 * by design) → undefined: nothing to explain.
 */
export type PlanAction = 'plan' | 'extra' | 'update';

const FALLBACK_LEAD: Record<PlanAction, string> = {
  plan: 'AI unavailable — this plan was built on your device instead.',
  extra: 'AI unavailable — the added step came from your device instead.',
  update: 'AI unavailable — your plan was updated on your device instead (steps from your note were added; nothing was reordered).',
};

export function fallbackReason(provider: unknown): string | undefined {
  const raw = (provider as { lastError?: unknown } | null)?.lastError;
  if (typeof raw !== 'string' || !raw.trim()) return undefined;
  const reason = raw.replace(/^Gemini unavailable — used local engine fallback:\s*/, '').trim();
  return reason || raw.trim();
}

export function planFallbackNotice(action: PlanAction, provider: unknown): string | undefined {
  const reason = fallbackReason(provider);
  if (!reason) return undefined;
  const clipped = reason.length > 220 ? `${reason.slice(0, 219)}…` : reason;
  return `${FALLBACK_LEAD[action]} Reason: ${clipped}`;
}
